// Numeric CAPPI at the exact selected acquisition time. No time borrowing.
export function createVolumeFrames(onChange) {
    const cache=new Map(),failed=new Map();let selected=null,generation=0,controller=null,closed=false;
    async function select(frame,enabled) {
        const epoch=enabled&&frame?.kind==='observed'?Number(frame.timeMs):null;
        if(epoch===selected||epoch&&performance.now()<(failed.get(epoch)||0))return;selected=epoch;const token=++generation;
        controller?.abort();controller=null;
        if(!epoch){onChange(null);return;}
        if(cache.has(epoch)){onChange(cache.get(epoch));return;}
        onChange(null);
        controller=new AbortController();const signal=controller.signal;
        try {
            const root='/v2/data/radar-volume/';
            const response=await fetch(`${root}${epoch}.json`,{signal});
            if(!response.ok)throw Error('Volume unavailable');
            const meta=await response.json();
            if(meta.schema!=='ws-radar-volume-v1'||meta.time_ms!==epoch||JSON.stringify(meta.levels_amsl_m)!=='[1000,2000,3000,5000]'||meta.width>1024||meta.height>1024||!Array.isArray(meta.mercator_bounds)||!meta.mercator_bounds.every(Number.isFinite)||meta.atlas!==`${epoch}.png`)throw Error('Unsupported volume');
            const body=await fetch(root+meta.atlas,{signal});if(!body.ok)throw Error('Atlas unavailable');
            const bitmap=await createImageBitmap(await body.blob());
            const canvas=document.createElement('canvas');canvas.width=bitmap.width;canvas.height=bitmap.height;
            if(bitmap.width!==meta.atlas_size[0]||bitmap.height!==meta.atlas_size[1]){bitmap.close();throw Error('Atlas shape mismatch');}
            const context=canvas.getContext('2d',{willReadFrequently:true});context.drawImage(bitmap,0,0);bitmap.close();
            const pixels=context.getImageData(0,0,canvas.width,canvas.height).data;
            const volume={meta,canvas,pixels,key:epoch};cache.set(epoch,volume);failed.delete(epoch);
            while(cache.size>4)cache.delete(cache.keys().next().value);
            if(!closed&&token===generation){onChange(volume);}
        }catch(error){if(error.name!=='AbortError'&&!closed&&token===generation){selected=null;failed.set(epoch,performance.now()+30000);if(failed.size>8)failed.delete(failed.keys().next().value);onChange(null);}}
    }
    // Read raw encoded surface estimates, not the rendered radar palette.
    function sample(volume,mx,my,tile=4) {
        const {meta,pixels}=volume,[w,n,e,s]=meta.mercator_bounds;
        if(mx<w||mx>e||my<n||my>s)return null;
        const x=Math.min(meta.width-1,Math.floor((mx-w)/(e-w)*meta.width)),y=Math.min(meta.height-1,Math.floor((my-n)/(s-n)*meta.height));
        const tx=(tile%2)*meta.tile_size[0]+1+x,ty=Math.floor(tile/2)*meta.tile_size[1]+1+y;
        const i=(ty*meta.atlas_size[0]+tx)*4;
        if(pixels[i+1]<254)return null;
        return tile===4?Math.expm1(pixels[i]/255*Math.log(201)):pixels[i]/255*112-32;
    }
    return {select,sample,close(){closed=true;++generation;controller?.abort();cache.clear();}};
}

// CPU twin of the shader's masked bilinear/vertical interpolation, for picking.
export function sampleVolumeEcho(volume,mx,my,height,allowCensored=false) {
    const {meta,pixels}=volume,[w,n,e,s]=meta.mercator_bounds;
    if(!allowCensored&&meta.censor_mask_channel!=='blue')return null;
    if(mx<w||mx>e||my<n||my>s||height<1000||height>5000)return null;
    const x=Math.max(0,Math.min(meta.width-1,(mx-w)/(e-w)*meta.width-.5)),y=Math.max(0,Math.min(meta.height-1,(my-n)/(s-n)*meta.height-.5));
    const x0=Math.floor(x),y0=Math.floor(y),x1=Math.min(meta.width-1,x0+1),y1=Math.min(meta.height-1,y0+1),fx=x-x0,fy=y-y0;
    function plane(tile){let code=0,coverage=0,censored=false;for(const [px,py,weight]of [[x0,y0,(1-fx)*(1-fy)],[x1,y0,fx*(1-fy)],[x0,y1,(1-fx)*fy],[x1,y1,fx*fy]]){
        const i=(((Math.floor(tile/2)*meta.tile_size[1]+1+py)*meta.atlas_size[0])+(tile%2)*meta.tile_size[0]+1+px)*4;
        if(weight>.001&&(pixels[i]===0||pixels[i+2]>0))censored=true;code+=pixels[i]/255*weight;coverage+=pixels[i+1]/255*weight;
    }return (!censored||allowCensored)&&coverage>=.999?code*112-32:null;}
    const level=height<2000?0:height<3000?1:2,a=plane(level),b=plane(level+1);
    if(a==null||b==null)return null;
    const z=meta.levels_amsl_m,t=(height-z[level])/(z[level+1]-z[level]);return a+(b-a)*t;
}
